import { useApp } from '@/context/AppContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import { HorizontalStackedBar } from '@/components/charts/HorizontalStackedBar'
import { buildFilterKey } from '@/services/filterEngine'
import { getResponseColor } from '@/utils/colors'

// Q16 - Beliefs and Doubts (Science/Health Literacy) - ONLY these 8 questions
// Ordered by agreement level (highest first) per topline
const Q16_BELIEFS_QUESTIONS = [
  { id: 'Agree_VitaminsSupplements', label: 'Vitamins and supplements are key to improving performance in daily life.' },
  { id: 'Agree_AltMedicineTrust', label: 'I trust alternative medicine (e.g., naturopathy, homeopathy, herbal remedies) more than mainstream medicine.' },
  { id: 'Agree_HumanClimateChange', label: 'Earth is now warming because of carbon pollution caused by humans.' },
  { id: 'Agree_CovidVaccines', label: 'Vaccinations against COVID-19 are an important tool in protecting the health of citizens.' },
  { id: 'Agree_LibertyRestrictPH', label: 'Governments should NOT have the right to restrict individual liberty in the name of public health.' },
  { id: 'Agree_OzoneClimateCause', label: 'Climate change is primarily caused by chemicals burning holes in the Earth\'s ozone layer.' },
  { id: 'Agree_WeatherModTech', label: 'The recent increase in extreme weather and wildfires is mostly caused by weather modification technologies.' },
  { id: 'Agree_HumansDinosaurs', label: 'The fossil record shows that humans were present on Earth during the age of dinosaurs.' },
] as const

// Response order: Strongly Agree → Agree → Unsure → Disagree → Strongly Disagree (case-insensitive)
// Note: Data has inconsistent neutral labels: "Neutral / Unsure", "Neutral /Unsure", "Neutral Unsure"
const AGREE_RESPONSE_ORDER = ['strongly agree', 'somewhat agree', 'agree', 'neutral / unsure', 'neutral /unsure', 'neutral unsure', 'unsure', 'neutral', 'disagree', 'somewhat disagree', 'strongly disagree']

// Sort responses according to agree/disagree order (Unsure in the middle) - case-insensitive matching
const sortAgreeResponses = (responses: Array<{ label: string; percentage: number }>) => {
  return [...responses].sort((a, b) => {
    const aIdx = AGREE_RESPONSE_ORDER.indexOf(a.label.toLowerCase())
    const bIdx = AGREE_RESPONSE_ORDER.indexOf(b.label.toLowerCase())
    if (aIdx === -1) return 1
    if (bIdx === -1) return -1
    return aIdx - bIdx
  })
}

// Legend labels (simplified for display)
const LEGEND_LABELS = ['Strongly Agree', 'Somewhat Agree', 'Neutral / Unsure', 'Somewhat Disagree', 'Strongly Disagree']

export function BeliefsAndDoubts() {
  const { state } = useApp()
  const { data, filters } = state
  const { selectedBreak, selectedProvince } = filters

  const breakDef = data?.breaks.find(b => b.id === selectedBreak)
  const { breakValues: filteredBreakValues } = useFilteredBreakValues()
  const breakValues = selectedBreak !== 'total' && breakDef ? filteredBreakValues : ['total']
  const isBreakActive = selectedBreak !== 'total'
  const breakName = breakDef?.name || 'Total'
  const displayMap = breakDef?.displayMap || {}
  const getDisplayName = (value: string) => displayMap[value] || value

  // Get ONLY Q16 Beliefs questions (8 specific questions about science/health literacy)
  const beliefsQuestions = data
    ? Q16_BELIEFS_QUESTIONS
        .filter(q => q.id in data.questions)
        .map(q => ({ id: q.id, label: q.label, question: data.questions[q.id] }))
    : []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
          Beliefs and Doubts
        </h1>
        <span className="text-sm text-slate-500 dark:text-slate-400">
          Break: {breakName}
        </span>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <p className="text-slate-600 dark:text-slate-300 mb-6">
          To what extent do you AGREE or DISAGREE with the following statements?
        </p>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-6 pb-4 border-b border-slate-100 dark:border-slate-700">
          {LEGEND_LABELS.map(label => (
            <div key={label} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: getResponseColor('likert5', label) }}
              />
              <span className="text-xs text-slate-600 dark:text-slate-400">{label}</span>
            </div>
          ))}
        </div>

        <div className="space-y-6">
          {!data ? (
            <p className="text-slate-500 dark:text-slate-400">Loading beliefs questions...</p>
          ) : isBreakActive ? (
            // Break is active - show multiple rows per question
            beliefsQuestions.map(({ id: questionId, label: questionLabel, question }) => {
              const rows = breakValues.map(value => {
                const filterKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined, selectedProvince)
                let questionData = question.data[filterKey]
                if (!questionData && selectedProvince) {
                  questionData = question.data[buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)]
                }
                return { value, filterKey, data: questionData }
              }).filter(row => row.data)

              if (rows.length === 0) return null

              return (
                <div key={questionId} className="border-b border-slate-100 dark:border-slate-700 pb-4 last:border-0 last:pb-0">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">{questionLabel}</h3>
                  <div className="space-y-2">
                    {rows.map(row => (
                      <div key={row.filterKey} className="flex items-center gap-2">
                        <div className="w-32 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0" title={getDisplayName(row.value)}>
                          {getDisplayName(row.value)}
                        </div>
                        <div className="flex-1">
                          <HorizontalStackedBar
                            segments={sortAgreeResponses(row.data!.responses)}
                            scaleType="likert5"
                            height={24}
                            showLabels={false}
                            normalize={true}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })
          ) : (
            // Total (no break) - show single bar per question with full labels
            beliefsQuestions.map(({ id: questionId, label: questionLabel, question }) => {
              const filterKey = buildFilterKey('total', 'total')
              const questionData = question.data[filterKey]

              if (!questionData) return null

              return (
                <div key={questionId} className="border-b border-slate-100 dark:border-slate-700 pb-4 last:border-0">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">{questionLabel}</h3>
                  <div className="flex items-center gap-2">
                    <div className="w-32 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0">Total</div>
                    <div className="flex-1">
                      <HorizontalStackedBar
                        segments={sortAgreeResponses(questionData.responses)}
                        scaleType="likert5"
                        height={28}
                        showLabels={false}
                        normalize={true}
                      />
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
